/* =============================================================================
   CORE / TABLE-OCCUPANCIES — Walk-In-Helpers (S60 / phase_51)
   =============================================================================
   3-PFAD-GARANTIE:
     · RESERVATION (Voraus-Buchung)   → reservations
     · WALK-IN     (jetzt-und-hier)   → table_occupancies   ◄── hier
     · DELIVERY    (außer Haus)       → orders mit service_type='delivery'

   Walk-Ins sind KEINE Reservations. Wer das Pattern in den Code zurückbringt,
   verstößt gegen die 3-Pfad-Trennung — DB blockt das via CHECK
   `reservations_source_no_walkin`.

   Helpers hier kapseln:
     · isOccupancyActive(o)              — Status-Check
     · occupancyDurationMin(o)           — Dauer in Minuten
     · isTableOccupiedNow(tableId, ...)  — gibt es eine aktive Belegung?
     · activeOccupanciesForTable(...)    — Liste aktive Belegungen für Tisch
============================================================================ */

export function isOccupancyActive(o) {
  return !!o && o.status === 'active' && !o.deletedAt;
}

export function occupancyDurationMin(o) {
  if (!o) return 0;
  const start = o.startedAt ? new Date(o.startedAt).getTime() : 0;
  const end   = o.endedAt   ? new Date(o.endedAt).getTime()   : Date.now();
  if (!start || end <= start) return 0;
  return Math.round((end - start) / 60000);
}

/**
 * S134c (Befund 1+4) — effektives Block-Ende einer Belegung in Minuten,
 * gespiegelt von capacity.effectiveReservationEndMin. Eine beendete Belegung
 * (`ended_at` gesetzt) schrumpft auf ihre reale Auscheck-Zeit; eine laufende
 * Belegung blockt bis `started_at + durationMin` (geplant). `startMin` ist die
 * Minuten-ab-Tagesbeginn der Start-Zeit (Single-Source für Timeline + Tests).
 *
 * Relativ zu startMin gerechnet (started→ended-Delta), damit über-Mitternacht-
 * Belegungen nicht kippen. Kein Min/Max-Clamp: ended_at ist die Wahrheit
 * (frühzeitig beendet → kürzer, Überzieher → länger als geplant).
 */
export function effectiveOccupancyEndMin(o, startMin, nowMin) {
  const base = Number(startMin) || 0;
  const planned = base + (Number(o && o.durationMin) || 90);
  const started = o && o.startedAt;
  const ended   = o && o.endedAt;
  if (started && ended) {
    const startedMs = new Date(started).getTime();
    const endedMs   = new Date(ended).getTime();
    if (!isNaN(startedMs) && !isNaN(endedMs) && endedMs > startedMs) {
      const plannedDur = Number(o && o.durationMin) || 90;
      const realDur    = Math.round((endedMs - startedMs) / 60000);
      // S196-Fix (Jonas): „eine Belegung endet spätestens mit Ende der Sitzdauer."
      // Früh-Geher (ended < geplant) schrumpfen weiter; ein späterer force-close
      // (ended_at Tage nach Start bei verwaisten Alt-Belegungen) darf den Balken
      // NICHT über die geplante Dauer hinausziehen — sonst Geister-Belegung an
      // Folgetagen (I7-Befund 02.07). Cappt bewusst das S134c-„Überzieher länger".
      return base + Math.min(realDur, plannedDur);
    }
  }
  // S196 Cut a — eine aktive Belegung ohne ended_at darf NIE vor „jetzt" enden.
  // Sonst verschwindet ein Walk-In, dessen geplante Dauer abgelaufen ist (gestern
  // gestartet, nie freigegeben), aus der Timeline (I2-Befund). Effektives Ende =
  // max(geplant, jetzt). nowMin ist optional (gleiche Minuten-ab-Tagesbeginn-Basis
  // wie startMin); ohne nowMin bleibt das alte Verhalten (geplant) für Tests/Nicht-
  // heute-Sichten.
  if (started && !ended && o && o.status === 'active' && Number.isFinite(nowMin)) {
    return Math.max(planned, nowMin);
  }
  return planned;
}

/**
 * S196 Cut a — effektives Block-Ende einer Belegung als Millisekunden-Timestamp.
 * Single-Source für alle absoluten Zeitfenster-Filter (Timeline dayWalkIns,
 * capacity getOccupiedTableIds / getTableSharingState).
 *
 * - beendet (ended_at): reale Auscheck-Zeit.
 * - aktiv ohne ended_at: max(now, started_at + durationMin) — laufende Belegung
 *   bleibt bis zur Freigabe sichtbar/blockierend (I2-Befund).
 * - sonst (z. B. completed ohne ended_at — Datenanomalie): geplantes Ende.
 *
 * @param {object} o     gemappte Belegung
 * @param {number} nowMs Referenz-„jetzt" (Default Date.now()) — injizierbar für Tests.
 * @returns {number}     Millisekunden-Timestamp oder NaN bei ungültigem Start.
 */
export function effectiveOccupancyEndMs(o, nowMs) {
  if (!o || !o.startedAt) return NaN;
  const startMs = new Date(o.startedAt).getTime();
  if (isNaN(startMs)) return NaN;
  const plannedMs = startMs + (Number(o.durationMin) || 90) * 60 * 1000;
  if (o.endedAt) {
    const endedMs = new Date(o.endedAt).getTime();
    // S196-Fix (Jonas): beendete Belegung endet spätestens mit Ende der Sitzdauer.
    // Früh-Geher → ended_at; späterer force-close → auf geplantes Ende gecappt
    // (keine Geister-Belegung an Folgetagen, I7-Befund).
    if (!isNaN(endedMs)) return Math.min(endedMs, plannedMs);
  }
  if (o.status === 'active') {
    const ref = Number.isFinite(nowMs) ? nowMs : Date.now();
    return Math.max(ref, plannedMs);
  }
  return plannedMs;
}

/**
 * S196 Cut a — ist eine aktive Belegung „überfällig"? (geplante Sitzdauer
 * abgelaufen, aber noch nicht freigegeben). Basis für den „⏰ überfällig"-Hinweis
 * in Timeline/Snapshot/Modal. Nach der Auto-Freigabe bleiben genau die überfälligen
 * Belegungen übrig, die eine offene Order tragen (oder im 15-Min-Puffer liegen).
 */
export function isOccupancyOverdue(o, nowMs) {
  if (!o || o.status !== 'active' || o.deletedAt || o.endedAt || !o.startedAt) return false;
  const startMs = new Date(o.startedAt).getTime();
  if (isNaN(startMs)) return false;
  const plannedMs = startMs + (Number(o.durationMin) || 90) * 60 * 1000;
  const ref = Number.isFinite(nowMs) ? nowMs : Date.now();
  return ref > plannedMs;
}

/**
 * S233 Ticket A — Minuten, die eine aktive Belegung ÜBER ihr geplantes
 * Sitzdauer-Ende hinaus läuft (für den „⏱ +N min"-Marker im Belegungsplan).
 * Basiert auf demselben geplanten Ende wie `isOccupancyOverdue` (started_at +
 * durationMin, Default 90 wie der Auto-Release-Cron). 0, wenn die Belegung nicht
 * überfällig ist (nicht aktiv / noch innerhalb der geplanten Dauer / kein Start).
 * Pure — `nowMs` injizierbar für Tests.
 * @returns {number} volle Minuten über dem geplanten Ende (≥ 0)
 */
export function occupancyOverdueMinutes(o, nowMs) {
  if (!isOccupancyOverdue(o, nowMs)) return 0;
  const startMs = new Date(o.startedAt).getTime();
  const plannedMs = startMs + (Number(o.durationMin) || 90) * 60 * 1000;
  const ref = Number.isFinite(nowMs) ? nowMs : Date.now();
  return Math.max(0, Math.round((ref - plannedMs) / 60000));
}

/**
 * Findet alle aktiven Walk-Ins für einen bestimmten Tisch.
 * Berücksichtigt sowohl table_id (Single-Tisch) als auch assignedTableIds
 * (Multi-Tisch-Gruppe / tables_combined).
 */
export function activeOccupanciesForTable(tableId, occupancies) {
  if (!tableId || !Array.isArray(occupancies)) return [];
  return occupancies.filter(o => {
    if (!isOccupancyActive(o)) return false;
    if (o.tableId === tableId) return true;
    if (Array.isArray(o.assignedTableIds) && o.assignedTableIds.includes(tableId)) return true;
    return false;
  });
}

/** Convenience: ist dieser Tisch gerade durch einen Walk-In belegt? */
export function isTableOccupiedNow(tableId, occupancies) {
  return activeOccupanciesForTable(tableId, occupancies).length > 0;
}

/**
 * S271 §A — Sweep-Line-Events der Walk-Ins in einem Zeitfenster (pure).
 *
 * Liefert [[minute, ±guests]] relativ zum Tagesbeginn von `dateIso`, auf
 * [startMin, endMin) geclippt. Mitternachts-sicher wie getOccupiedTableIds:
 * das Fenster wird in Millisekunden gerechnet (Tagesbeginn + Minuten), eine
 * Belegung, die am Vortag begann und ins Fenster hineinragt, zählt also mit.
 * Das effektive Ende ist `effectiveOccupancyEndMs` (aktive Belegung endet nie
 * vor „jetzt"). Dieselbe Regel steckt serverseitig in enforce_reservation_capacity
 * (phase_201).
 *
 * Gezählt werden nur ECHTE Walk-Ins (ohne reservationId): eine eingecheckte
 * Reservierung zählt bereits über die Reservierung selbst (confirmed/completed),
 * sonst stünde sie doppelt in der Summe.
 *
 * @param {object} [opts] { nowMs, excludeId }
 */
export function walkInEventsInWindow(dateIso, startMin, endMin, occupancies, opts = {}) {
  if (!dateIso || !Array.isArray(occupancies) || !(endMin > startMin)) return [];
  const dayStartMs = new Date(dateIso + 'T00:00:00').getTime();
  if (isNaN(dayStartMs)) return [];
  const winStartMs = dayStartMs + startMin * 60000;
  const winEndMs   = dayStartMs + endMin * 60000;
  const events = [];
  for (const o of occupancies) {
    if (!o || o.status !== 'active' || o.deletedAt) continue;
    if (opts.excludeId && o.id === opts.excludeId) continue;
    if (o.reservationId) continue;
    if (!o.startedAt) continue;
    const startMs = new Date(o.startedAt).getTime();
    if (isNaN(startMs)) continue;
    const endMs = effectiveOccupancyEndMs(o, opts.nowMs);
    if (isNaN(endMs)) continue;
    if (startMs >= winEndMs || endMs <= winStartMs) continue;
    const guests = Number(o.guests) || 0;
    if (guests <= 0) continue;
    const clipStart = Math.max(startMs, winStartMs);
    const clipEnd   = Math.min(endMs, winEndMs);
    events.push([(clipStart - dayStartMs) / 60000, guests]);
    events.push([(clipEnd - dayStartMs) / 60000, -guests]);
  }
  return events;
}

/**
 * Spitzenwert einer Event-Liste [[t, ±delta]] — End-Events vor Start-Events bei
 * gleichem Zeitpunkt (kein künstlicher Peak an Slot-Grenzen). Pure; geteilt von
 * Reservierungen (capacity.maxSimultaneousInWindow) und Walk-Ins.
 */
export function peakOfEvents(events) {
  if (!Array.isArray(events) || events.length === 0) return 0;
  const sorted = events.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  let cur = 0, max = 0;
  for (const [, delta] of sorted) {
    cur += delta;
    if (cur > max) max = cur;
  }
  return max;
}

/**
 * Spitzenwert gleichzeitig anwesender Walk-In-Gäste in einem Zeitfenster —
 * das Walk-In-Gegenstück zu capacity.maxSimultaneousInWindow.
 *
 * S271 §A: war bis dahin eine Summe aller überlappenden Belegungen (zählte
 * sequenzielle Walk-Ins doppelt) ohne Mitternachts-Clipping; jetzt derselbe
 * Sweep wie bei Reservierungen über walkInEventsInWindow/peakOfEvents.
 *
 * @param dateIso 'YYYY-MM-DD' (Lokalzeit)
 * @param startMin Minuten ab Tagesbeginn
 * @param endMin   Minuten ab Tagesbeginn
 * @param occupancies gemappte Belegungen
 * @param {object} [opts] { nowMs, excludeId }
 */
export function simultaneousWalkinsInWindow(dateIso, startMin, endMin, occupancies, opts = {}) {
  return peakOfEvents(walkInEventsInWindow(dateIso, startMin, endMin, occupancies, opts));
}

/** Permission-Side-Effect: window-Export für Test-Hooks. */
if (typeof window !== 'undefined') {
  window.App = window.App || {};
  window.App.tableOccupanciesHelpers = {
    isOccupancyActive,
    occupancyDurationMin,
    effectiveOccupancyEndMin,
    effectiveOccupancyEndMs,
    isOccupancyOverdue,
    occupancyOverdueMinutes,
    activeOccupanciesForTable,
    isTableOccupiedNow,
    walkInEventsInWindow,
    peakOfEvents,
    simultaneousWalkinsInWindow
  };
}
